<template>
  <canvas
    class="map-instance"
    ref="canvasMain"
    :class="computedPointerClassname"
    @mousemove="handlePointerMove"
    @touchmove="handlePointerMove"
    @mousedown="handlePointerDown"
    @touchstart="handlePointerDown"
    @mouseup="handlePointerUp"
    @touchend="handlePointerUp"
    @dblclick="handlePointerDoubleClick"
    @contextmenu="handlePointerContextMenu"
  ></canvas>
  
  <canvas
    class="map-instance map-instance--highlights"
    ref="canvasHighlights"
  ></canvas>
  
  <template v-if="contexts.ctx">
    <user-pointer
      v-for="visitor in visitors"
      :key="visitor.id"
      :visitor="visitor"
      :ctx="contexts.ctx"
      :zoom-level="zoomLevel"
      :pixel-ratio="pixelRatio"
    />
  </template>
  
  <template v-if="contexts.ctx">
    <quick-edit-bar
      :ctx="contexts.ctx"
      :zoom-level="zoomLevel"
      :pixel-ratio="pixelRatio"
    />
  </template>

  <shortcuts-overlay />
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import type { Emitter } from 'mitt';
import {
  useCanvasManager,
  usePointer,
  useMapArea,
  useElements,
  useZoomManager,
  useKeyboardShortcuts,
  QuickEditBar,
  ShortcutsOverlay,
  UserPointer
} from '@/modules/map-editor';
import type { Node, Edge, MapImageConfig } from '@/modules/map-editor';

interface Props {
  mapId: number | string;
  isMapOnFocus?: boolean;
  nodes: Node[];
  edges: Edge[];
  layersAmount: number;
  // layerCurrentId: number;
  selectedEdgeId: number | null;
  selectedNodes: Node[];
  mapData: MapImageConfig;
}
const props = defineProps<Props>();
const store = useStore();

const layerCurrentId = computed(() => Number(store.getters['layers/getCurrentLayerId']));

const visitors = computed(() => store.getters['simultaneousEditing/getVisitors']);

// Map area service
const { mapAreaService, transferMapArea, cancelTransfer, updateMapAreaCoords, saveWindowDimensions } = useMapArea();

// Watch bg color
watch(() => props.mapData.bgColor, (newColor) => {
  if (canvasMain.value) {
    setCanvasBackgroundColor(`${newColor}`);
  }
});

// Canvas management
const {
  canvasMain,
  canvasHighlights,
  contexts,
  pixelRatio,
  initOffscreenCanvas,
  initContexts,
  resizeAllCanvases,
  updatePixelRatio,
  setDataGetters,
  setCanvasBackgroundColor,
  startAnimation,
  stopAnimation,
  cleanup
} = useCanvasManager(mapAreaService);

// Elements management
const { elementsInit } = useElements(mapAreaService);

// Zoom management
const layersAmountRef = computed(() => props.layersAmount);
const {
  zoomLevel,
  cancelZoomAnimation
} = useZoomManager(
  layersAmountRef,
  layerCurrentId,
  mapAreaService
);

// Pointer handling
const {
  computedPointerClassname,
  selectionBox,
  isHoveringSelectionBox,
  pointerMove,
  pointerDown,
  pointerUp,
  pointerDoubleClick,
  pointerContextMenu,
  handleKeyDown,
  handleKeyUp
} = usePointer();

// Canvas-level editing shortcuts (delete, duplicate, nudge, select-all, …)
const { handleShortcutKeyDown } = useKeyboardShortcuts();

// Emitter for events
const emitter = inject("emitter")  as Emitter<any>;

function handlePointerMove(event: MouseEvent | TouchEvent) {
  pointerMove(
    event,
    contexts.value,
    canvasMain.value,
    zoomLevel.value
  );
}

function handlePointerDown(event: MouseEvent | TouchEvent) {
  pointerDown(
    event,
    contexts.value,
    canvasMain.value,
    zoomLevel.value
  );
}

function handlePointerUp(event: MouseEvent | TouchEvent) {
  pointerUp(
    event,
    contexts.value,
    canvasMain.value,
    zoomLevel.value
  );
}

function handlePointerDoubleClick(event: MouseEvent) {
  pointerDoubleClick(event, contexts.value, zoomLevel.value,);
}

function handlePointerContextMenu(event: MouseEvent) {
  pointerContextMenu(event);
}

/**
 * Map reset handler - called when emitter triggers 'mapReset'
 */
async function handleMapReset(): Promise<void> {
  // Resize canvases
  resizeAllCanvases();
  
  // Center map position
  transferMapArea();
  
  // Reinitialize elements (avoid populate since data is already loaded)
  await elementsInit({ avoidPopulate: true });
  
  // Trigger full redraw
  mapAreaService.updateElement(true);
}

/**
 * Window resize handler
 */
function handleWindowResize(): void {
  const pixelRatioOld = pixelRatio.value;
  updatePixelRatio();
  resizeAllCanvases();
  updateMapAreaCoords(pixelRatio.value);
  mapAreaService.updateElement(true);

  if (pixelRatio.value !== pixelRatioOld) {
    elementsInit({ avoidPopulate: true });
  }
}

// Initialize component
onMounted(() => {
  // Initialize offscreen canvas
  initOffscreenCanvas();
  
  // Initialize contexts
  initContexts();
  
  // Resize all canvases
  resizeAllCanvases();

  // Snapshot initial window dimensions for resize repositioning
  saveWindowDimensions(pixelRatio.value);

  setDataGetters({
    nodes: () => props.nodes,
    edges: () => props.edges,
    layerId: () => layerCurrentId.value,
    selectedEdgeId: () => props.selectedEdgeId,
    selectedNodes: () => props.selectedNodes,
    mapData: () => props.mapData,
    zoomLevel: () => zoomLevel.value,
    selectionBox: () => selectionBox.value,
    isHoveringSelectionBox: () => isHoveringSelectionBox.value
  });
  
  // Start animation loop
  startAnimation();
  
  if (canvasMain.value) {
    setCanvasBackgroundColor(`${props.mapData.bgColor}`);
  }
  
  // Add event listeners
  window.addEventListener('resize', handleWindowResize);
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);
  window.addEventListener('keydown', handleShortcutKeyDown);

  // Register mapReset event handler
  emitter.on('mapReset', handleMapReset);
});

onUnmounted(() => {
  // Cleanup
  cancelTransfer();
  cancelZoomAnimation();
  stopAnimation();
  cleanup();
  
  // Remove event listeners
  window.removeEventListener('resize', handleWindowResize);
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('keyup', handleKeyUp);
  window.removeEventListener('keydown', handleShortcutKeyDown);

  // Remove event handler
  emitter.off('mapReset', handleMapReset);
});
</script>

<style lang="scss">
@use "@/scss/sections/editor/map/map-instance";

.option-custom {
  position: fixed;
  height: 42px;
  color: white;
  right: 20px;
  bottom: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  background: #171717;
  cursor: pointer;
  padding: 0 15px;
  
  &.edge {
    bottom: 110px;
  }
}
</style>